import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { PieChart, Series, Legend, Title, Tooltip } from 'devextreme-react/pie-chart'
import {
	Chart,
	Series as ChartSeries,
	Legend as ChartLegend,
	Title as ChartTitle,
	Tooltip as ChartTooltip,
} from 'devextreme-react/chart'
import ModelPage from '../model/model'
import axios from 'axios'
import notify from '../../utils/notify'
import './dashboard.scss'

interface ChartData {
	arg: string
	val: number
}

export default function Dashboard() {
	const [searchParams] = useSearchParams()
	const folder = searchParams.get('folder')
	const model = searchParams.get('model')

	const [chartData, setChartData] = useState<ChartData[]>([])
	const [chartLoading, setChartLoading] = useState(true)
	const [chartField, setChartField] = useState<string>('')
	const [modelText, setModelText] = useState<string>('')
	const [pieChartData, setPieChartData] = useState<string[]>([])
	const [columnSums, setColumnSums] = useState<any>(null)
	const [columnSumsLoading, setColumnSumsLoading] = useState(true)
	const [pieChartTitle, setPieChartTitle] = useState<string>('')
	const [barChartTitle, setBarChartTitle] = useState<string>('')
	const [dashboardDataLoading, setDashboardDataLoading] = useState(true)

	const loadDashboardData = useCallback(async () => {
		setDashboardDataLoading(true)
		try {
			axios
				.get(`${API_FIBER_URL}/view/fields?folder=${folder}&model=${model}`)
				.then(response => response.data)
				.then(data => {
					setChartField(data.data.dashboard.barChart.field)
					setPieChartData(data.data.dashboard.pieChart.fields)
					setPieChartTitle(data.data.dashboard.pieChart.title)
					setBarChartTitle(data.data.dashboard.barChart.title)
					setModelText(data.data.text || model)
				})
		} catch (error) {
			notify('Failed to load model data', 'error')
		} finally {
			setDashboardDataLoading(false)
		}
	}, [folder, model])

	const loadChartData = useCallback(async () => {
		setChartLoading(true)
		try {
			if (model) {
				const response = await axios.get(`${API_FIBER_URL}/dashboard/fieldValueCounts`, {
					params: {
						folder: folder,
						model: model,
						field: chartField,
					},
				})

				const data = response.data
				if (data.success && data.valueCounts) {
					const chartDataFromAPI = data.valueCounts.map((item: any) => ({
						arg: item.value || 'Unknown',
						val: item.count,
					}))
					setChartData(chartDataFromAPI)
				} else {
					setChartData([])
				}
			}
		} catch (error) {
			notify('Failed to load chart data', 'error')
			setChartData([])
		} finally {
			setChartLoading(false)
		}
	}, [folder, model, chartField])

	const loadColumnSums = useCallback(
		async (columns: string[]) => {
			if (!model || !columns.length) {
				setColumnSumsLoading(false)
				return
			}
			setColumnSumsLoading(true)
			try {
				const response = await axios.post(`${API_FIBER_URL}/dashboard/sumColumns`, {
					model: model,
					columns: columns,
				})
				if (response.data.success) {
					setColumnSums(response.data.sums)
				} else {
					setColumnSums(null)
				}
			} catch (error) {
				notify('Failed to load column sums', 'error')
				setColumnSums(null)
			} finally {
				setColumnSumsLoading(false)
			}
		},
		[model]
	)

	useEffect(() => {
		if (model) {
			loadDashboardData()
		}
	}, [model, loadDashboardData])

	useEffect(() => {
		if (model && pieChartData.length > 0) {
			loadColumnSums(pieChartData)
		}
	}, [model, pieChartData, loadColumnSums])

	useEffect(() => {
		if (model && chartField) {
			loadChartData()
		}
	}, [model, chartField, loadChartData])

	const breadcrumb = (folders: any) => {
		let path = ''
		return (
			folders &&
			folders.split('/').map((folder: string) => {
				path += `/${folder}`
				return (
					<React.Fragment key={path}>
						<Link className="link" to={`/v${path}`}>
							{folder}
						</Link>{' '}
						/{' '}
					</React.Fragment>
				)
			})
		)
	}

	const ChartLoadingSkeleton = ({ title }: { title: string }) => (
		<div className="chart-container">
			<div className="chart-card">
				<Title text={title} />
				<div className="chart-loading-skeleton">
					<div className="loading-spinner">Loading...</div>
				</div>
			</div>
		</div>
	)

	const DataDistributionChart = ({ chartData, fieldName }: { chartData: ChartData[]; fieldName?: string }) => {
		return (
			<div className="chart-container">
				<div className="chart-card">
					<Title text={pieChartTitle} />
					<PieChart dataSource={chartData} palette="Harmony Light" title={pieChartTitle}>
						<Series argumentField="arg" valueField="val" />
						<Legend visible={true} />
						<Tooltip enabled={true} />
					</PieChart>
				</div>
			</div>
		)
	}

	const DataBarChart = ({ chartData, fieldName }: { chartData: ChartData[]; fieldName?: string }) => {
		const sortedChartData = [...chartData].sort((a, b) => a.val - b.val)

		const colors = [
			'#7abd5c',
			'#FF6384',
			'#FFCE56',
			'#4BC0C0',
			'#9966FF',
			'#FF9F40',
			'#C9CBCF',
			'#B4FF9F',
			'#FFB4B4',
			'#B4D4FF',
			'#FFD6A5',
			'#A5FFD6',
			'#FFA5D6',
		]

		const customizePoint = (pointInfo: any) => {
			const idx = sortedChartData.findIndex(item => item.arg === pointInfo.argument)
			if (pointInfo.argument === 'Expired') {
				return { color: '#e18e92' }
			}
			return {
				color: colors[idx % colors.length],
			}
		}

		return (
			<div className="chart-container">
				<div className="chart-card">
					<ChartTitle text={barChartTitle} />
					<Chart
						dataSource={sortedChartData}
						palette="Soft Pastel"
						title={barChartTitle}
						customizePoint={customizePoint}
					>
						<ChartSeries argumentField="arg" valueField="val" type="bar" />
						<ChartLegend visible={false} />
						<ChartTooltip enabled={true} />
					</Chart>
				</div>
			</div>
		)
	}

	return (
		<div className="dashboard-container">
			<div className="header-chart-container">
				<div className="header-main">
					<div className="breadcrumb-section">
						<p className={'content-block'}>
							{breadcrumb(folder)} {modelText || (dashboardDataLoading ? 'Loading...' : model)}
						</p>
					</div>
				</div>
			</div>
			<div className="dashboard-content">
				<div className="dashboard-overview">
					<div className="charts-section">
						{columnSumsLoading ? (
							<ChartLoadingSkeleton title={pieChartTitle || 'Data Distribution'} />
						) : (
							columnSums && (
								<DataDistributionChart
									chartData={Object.entries(columnSums).map(([col, sum]) => ({
										arg: col,
										val: Number(sum),
									}))}
									fieldName="Sum of Machines by Status"
								/>
							)
						)}
						{chartLoading ? (
							<ChartLoadingSkeleton title={barChartTitle || 'Data Analysis'} />
						) : (
							<DataBarChart chartData={chartData} fieldName={chartField} />
						)}
					</div>
					<div className="model-wrapper">
						<ModelPage />
					</div>
				</div>
			</div>
		</div>
	)
}
